// --- AttendeesList.tsx (replace the whole component) ---
import {
  MeetingTutorLimitedResponse,
  MeetingTutorAdminResponse,
  Auth0AccountRole,
  TutorsService,
  StudentsService,
  TutorLimitedResponse,
  MeetingAdminResponse,
  MeetingLimitedResponse,
  TutorAdminResponse,
  MeetingStudentLimitedResponse,
  MeetingStudentAdminResponse,
  StudentAdminResponse,
  StudentLimitedResponse,
  MeetingsService,
  OrganizationSubjectAdminResponse,
  OrganizationSubjectPublicResponse,
  AvailabilitiesService,
  AvailableTutorsMessage,
  TutorStudentsService,
} from "client/openapi";

import {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  useRef,
} from "react";
import { Select, SelectWithAvailability } from "components/Select";
import { concatenateName } from "util/concatenateName";
import { useAuth0 } from "@auth0/auth0-react";
import CheckboxInput from "components/Inputs/CheckboxInput";
import MeetingAttendee from "components/MeetingAttendee";
import Notifications from "util/notifications";
import { announceMeetingsChanged } from "util/meetingsChanged";
import { OrgRolesAndAccountContext } from "util/OrgRolesAccountContext";
import { TutorContext } from "components/Dashboard/TutorDashboard/tutorContext";
import { MeetingDetails } from "components/MeetingDialog";
import moment from "moment";
import { TagColor, TagSize } from "components/Tag";
import { Tag } from "components/Tag/TagChip";
import { Dialog, DialogTrigger } from "components/Dialog";
import { Button, ButtonColor, ButtonFill, ButtonSize } from "components/Button";
import ComposeMessageDialog from "components/Dashboard/ComposeMessageDialog";
import MeetingFinancialsDialog from "components/MeetingDialog/Popups/financials";
import { FaDollarSign } from "react-icons/fa";
import { RecentKind } from "client/openapi/models/RecentKind";
import {
  RECENT_LABEL,
  RECENT_SHOWN,
  recentAsPerson,
  useRecentPeople,
} from "util/recentPeople";

// ---------- helpers (ids, unique, filters)

export const getAttendeeId = (attendee: any): number => {
  return "student" in attendee
    ? attendee.student.id
    : "tutor" in attendee
    ? attendee.tutor.id
    : attendee.id;
};

const getAttendeeOrgId = (attendee: any): number | undefined => {
  return "student" in attendee
    ? attendee.student.org_id
    : "tutor" in attendee
    ? attendee.tutor.org_id
    : attendee.org_id;
};

const uniqById = <T extends { id: number }>(items: T[]): T[] => {
  const map = new Map<number, T>();
  items.forEach((i) => map.set(i.id, i));
  return Array.from(map.values());
};

const removeSelected = <T extends { id: number }>(
  items: T[],
  selectedIds: number[]
) => items.filter((i) => !selectedIds.includes(i.id));

// ---------- service wrappers (use your real endpoints)

async function searchStudentsByName(
  orgId: number,
  phrase: string,
  role: Auth0AccountRole
) {
  if (role === Auth0AccountRole.ORG_TUTOR) {
    return StudentsService.getStudentsByOrganizationIfTutor({ orgId, phrase });
  }
  return StudentsService.getStudentsByOrganizationIfAdmin({ orgId, phrase });
}

async function searchTutorsByName(orgId: number, phrase: string) {
  return TutorsService.getTutorsByOrganization({ orgId, phrase });
}

async function getStudentsByTutor(tutorId: number) {
  return TutorStudentsService.getAllStudentsAssignedToATutor({ tutorId });
}

async function getTutorsByStudent(studentId: number) {
  return TutorStudentsService.getAllTutorsAssignedToAStudent({ studentId });
}

export function AttendeesList({
  attendees,
  typeOfAttendees,
  editing,
  creating,
  event,
  subject,
  linkPrefix,
  currentMeetingInfo,
  orgId,
  remove,
  updateAttendees,
  append,
}: {
  attendees: (
    | MeetingTutorLimitedResponse
    | MeetingStudentLimitedResponse
    | MeetingTutorAdminResponse
    | MeetingStudentAdminResponse
    | StudentLimitedResponse
    | StudentAdminResponse
    | TutorLimitedResponse
    | TutorAdminResponse
  )[];
  typeOfAttendees: Auth0AccountRole;
  editing: boolean;
  creating: boolean;
  event?: MeetingAdminResponse | MeetingLimitedResponse;
  subject?:
    | OrganizationSubjectPublicResponse
    | OrganizationSubjectAdminResponse;
  linkPrefix: string;
  currentMeetingInfo: MeetingDetails;
  orgId: number;
  remove: (userType: Auth0AccountRole, id: number) => void;
  updateAttendees: (
    userType: Auth0AccountRole,
    attendees: (
      | MeetingTutorLimitedResponse
      | MeetingStudentLimitedResponse
      | MeetingTutorAdminResponse
      | MeetingStudentAdminResponse
      | StudentLimitedResponse
      | StudentAdminResponse
      | TutorLimitedResponse
      | TutorAdminResponse
    )[]
  ) => void;
  append?: ReactNode[];
}) {
  const { user } = useAuth0();
  const { currently_selected_role, currently_selected_organization } =
    useContext(OrgRolesAndAccountContext);
  // The people this admin dealt with lately (admins only), listed after the
  // suggestions while nothing is typed. Only asked for while picking.
  const recent = useRecentPeople(
    editing
      ? [
          typeOfAttendees === Auth0AccountRole.ORG_TUTOR
            ? RecentKind.TUTOR
            : RecentKind.STUDENT,
        ]
      : []
  );

  const { current_tutor, available_tutors } = useContext(TutorContext);
  const [tutorsWithAvailability, setTutorsWithAvailability] =
    useState<AvailableTutorsMessage>();

  // Separate buckets
  const [suggestedOptions, setSuggestedOptions] = useState<
    | TutorLimitedResponse[]
    | StudentLimitedResponse[]
    | TutorAdminResponse[]
    | StudentAdminResponse[]
  >([]);
  const [searchResults, setSearchResults] = useState<
    | TutorLimitedResponse[]
    | StudentLimitedResponse[]
    | TutorAdminResponse[]
    | StudentAdminResponse[]
  >([]);

  const [inputValue, setInputValue] = useState("");
  const [tutorCorrespondingToThisMeeting, setTutorCorrespondingToThisMeeting] =
    useState<TutorLimitedResponse | TutorAdminResponse>();

  console.log("attendees", attendees);

  // ⬇️ filter helpers / computed selections STRICT to current org
  const isSameOrg = useCallback(
    (a: any) => getAttendeeOrgId(a) === orgId,
    [orgId]
  );

  const filteredAttendees = useMemo(
    () => attendees.filter(isSameOrg),
    [attendees, isSameOrg]
  );

  // Selected ids from the *prop* (single source of truth), filtered to org
  const selectedIds = useMemo(
    () => filteredAttendees.map((a) => getAttendeeId(a)),
    [filteredAttendees]
  );

  // For showing grouped options when not searching
  const isSearching = inputValue.trim().length > 0;

  // Resolve the tutor that corresponds to the current org
  useEffect(() => {
    if (available_tutors && current_tutor) {
      setTutorCorrespondingToThisMeeting(
        event ? available_tutors.find((t) => t.org_id === orgId) : current_tutor
      );
    }
  }, [available_tutors, current_tutor, event, orgId]);

  // ⬇️ Prune any cross‑org attendees defensively
  useEffect(() => {
    if (filteredAttendees.length !== attendees.length) {
      updateAttendees(typeOfAttendees, filteredAttendees);
    }
  }, [attendees, filteredAttendees, typeOfAttendees, updateAttendees]);

  // Auto-select current org tutor on create (tutor view), with strong guards to avoid dupes
  useEffect(() => {
    if (
      !creating ||
      currently_selected_role !== Auth0AccountRole.ORG_TUTOR ||
      typeOfAttendees !== Auth0AccountRole.ORG_TUTOR ||
      !tutorCorrespondingToThisMeeting
    ) {
      return;
    }

    // Only add when the in-org list is empty and this tutor isn't already selected
    const alreadySelectedThisTutor = filteredAttendees.some(
      (a) => getAttendeeId(a) === tutorCorrespondingToThisMeeting.id
    );

    if (filteredAttendees.length === 0 && !alreadySelectedThisTutor) {
      updateAttendees(typeOfAttendees, [
        ...filteredAttendees,
        tutorCorrespondingToThisMeeting,
      ]);
    }
  }, [
    creating,
    filteredAttendees,
    typeOfAttendees,
    currently_selected_role,
    tutorCorrespondingToThisMeeting,
    updateAttendees,
  ]);

  // When org changes, clear local caches (not the selected list; parent handles that)
  const prevOrgIdRef = useRef<number | undefined>(orgId);
  useEffect(() => {
    const prev = prevOrgIdRef.current;
    if (prev !== undefined && prev !== orgId) {
      setSuggestedOptions([]);
      setSearchResults([]);
      setInputValue("");
      setTutorsWithAvailability(undefined);
    }
    prevOrgIdRef.current = orgId;
  }, [orgId]);

  const handleAttendeesUpdate = useCallback(
    (option) => {
      // ignore accidental cross‑org options
      if (!isSameOrg(option)) return;
      const updated = [...filteredAttendees, option];
      updateAttendees(typeOfAttendees, updated);
    },
    [filteredAttendees, updateAttendees, typeOfAttendees, isSameOrg]
  );

  const handleAttendeeRemove = (id: number) => {
    const filtered = attendees.filter((a) => getAttendeeId(a) !== id);
    updateAttendees(typeOfAttendees, filtered);
    remove(typeOfAttendees, id);
  };

  // A tutor booking their own meeting stays on it.
  const isFixed = (attendee) =>
    typeOfAttendees === Auth0AccountRole.ORG_TUTOR &&
    tutorCorrespondingToThisMeeting?.id === getAttendeeId(attendee);

  // While editing, the chosen people are chips in the select: picking adds,
  // a chip's x (or Backspace) removes.
  const handleSelectionChange = (_selected, action) => {
    if (action.action === "select-option" && action.option) {
      handleAttendeesUpdate(action.option);
    } else if (
      (action.action === "remove-value" || action.action === "pop-value") &&
      action.removedValue &&
      !action.removedValue.isFixed
    ) {
      handleAttendeeRemove(getAttendeeId(action.removedValue));
    }
  };

  // Availability shading for tutors dropdown
  useEffect(() => {
    if (typeOfAttendees === Auth0AccountRole.ORG_TUTOR) {
      AvailabilitiesService.getTutorsByAvailability({
        orgId,
        start: moment(currentMeetingInfo.start)
          .startOf("day")
          .utc()
          .toISOString(),
        until: moment(currentMeetingInfo.start)
          .add(1, "day")
          .startOf("day")
          .utc()
          .toISOString(),
        exactStart: moment(currentMeetingInfo.start).utc().toISOString(),
        duration: currentMeetingInfo.duration,
      })
        .then(setTutorsWithAvailability)
        .catch(() => setTutorsWithAvailability(undefined));
    }
  }, [
    currentMeetingInfo.duration,
    currentMeetingInfo.start,
    orgId,
    typeOfAttendees,
  ]);

  // Build SUGGESTED options
  useEffect(() => {
    let cancelled = false;

    async function buildSuggestions() {
      try {
        if (!user || !currently_selected_role) {
          if (!cancelled) setSuggestedOptions([]);
          return;
        }

        const filterOutSelected = <T extends { id: number }>(list: T[]) =>
          removeSelected(list, selectedIds);

        // TUTOR VIEW
        if (currently_selected_role === Auth0AccountRole.ORG_TUTOR) {
          if (typeOfAttendees === Auth0AccountRole.ME) {
            // Use the org-aware endpoint that respects tutors_can_view_unassigned_students
            const students =
              await StudentsService.getStudentsByOrganizationIfTutor({
                orgId,
              })
                .then((r) => r)
                .catch(() => []);
            if (!cancelled)
              setSuggestedOptions(
                filterOutSelected(students.filter(isSameOrg))
              );
          } else {
            const allTutors = (available_tutors || []).filter(
              (t) => t.org_id === orgId
            );
            if (!cancelled) setSuggestedOptions(filterOutSelected(allTutors));
          }
          return;
        }

        // ADMIN VIEW
        if (typeOfAttendees === Auth0AccountRole.ME) {
          const tutorIds = currentMeetingInfo.tutor_ids || [];
          if (tutorIds.length === 0) {
            if (!cancelled) setSuggestedOptions([]);
            return;
          }
          const all = await Promise.all(
            tutorIds.map((tId: number) =>
              getStudentsByTutor(tId)
                .then((r) => r)
                .catch(() => [])
            )
          );
          const merged = uniqById(all.flat().filter(isSameOrg) as any[]);
          if (!cancelled) setSuggestedOptions(filterOutSelected(merged));
        } else {
          const studentIds = currentMeetingInfo.student_ids || [];
          const fromStudentsAll = await Promise.all(
            studentIds.map((sId: number) =>
              getTutorsByStudent(sId)
                .then((r) => r)
                .catch(() => [])
            )
          );
          const fromStudents = fromStudentsAll.flat().filter(isSameOrg);

          let fromSubject: (TutorLimitedResponse | TutorAdminResponse)[] = [];
          if (subject?.id) {
            fromSubject = await TutorsService.getTutorsBySubject({
              subjectId: subject.id,
            })
              .then((r) => r)
              .catch(() => []);
          }
          fromSubject = fromSubject.filter(isSameOrg);

          const merged = uniqById([...(fromStudents as any[]), ...fromSubject]);
          if (!cancelled) setSuggestedOptions(filterOutSelected(merged));
        }
      } catch (e) {
        console.error("Failed to build suggestions:", e);
        if (!cancelled) setSuggestedOptions([]);
      }
    }

    buildSuggestions();
    return () => {
      cancelled = true;
    };
  }, [
    user,
    orgId,
    available_tutors,
    selectedIds,
    typeOfAttendees,
    currently_selected_role,
    currentMeetingInfo.student_ids,
    currentMeetingInfo.tutor_ids,
    subject?.id,
    current_tutor?.id,
  ]);

  // Remote search for ADMIN and TUTOR roles
  useEffect(() => {
    let cancelled = false;
    const q = inputValue.trim();
    if (
      currently_selected_role !== Auth0AccountRole.ORG_ADMIN &&
      currently_selected_role !== Auth0AccountRole.ORG_TUTOR
    ) {
      setSearchResults([]);
      return;
    }
    if (q.length === 0) {
      setSearchResults([]);
      return;
    }

    const handle = window.setTimeout(async () => {
      try {
        const results =
          typeOfAttendees === Auth0AccountRole.ORG_TUTOR
            ? await searchTutorsByName(orgId, q)
            : await searchStudentsByName(orgId, q, currently_selected_role);

        if (!cancelled) {
          const inOrg = (results as any[]).filter(isSameOrg);
          setSearchResults(removeSelected(inOrg, selectedIds));
        }
      } catch (e) {
        console.error("name search failed", e);
        if (!cancelled) setSearchResults([]);
      }
    }, 250);

    return () => {
      cancelled = true;
      window.clearTimeout(handle);
    };
  }, [
    inputValue,
    orgId,
    selectedIds,
    typeOfAttendees,
    currently_selected_role,
  ]);

  // Remove current tutor from tutor options in tutor view
  const removeSelfIfTutor = useCallback(
    (items: any[]) => {
      if (currently_selected_role !== Auth0AccountRole.ORG_TUTOR) return items;
      return items.filter(
        (attendee) =>
          getAttendeeId(attendee) !== tutorCorrespondingToThisMeeting?.id
      );
    },
    [currently_selected_role, tutorCorrespondingToThisMeeting?.id]
  );

  // The recent people not chosen or suggested already, for this meeting's
  // organization.
  const recentOptions = useMemo(() => {
    if (orgId !== currently_selected_organization) return [];
    const listed = new Set([
      ...selectedIds,
      ...(suggestedOptions as any[]).map(getAttendeeId),
    ]);
    return recent
      .filter((person) => person.id && !listed.has(person.id))
      .slice(0, RECENT_SHOWN)
      .map((person) => recentAsPerson(person, orgId));
  }, [
    recent,
    orgId,
    currently_selected_organization,
    selectedIds,
    suggestedOptions,
  ]);

  // Compose options
  const groupedOptions = useMemo(() => {
    const base =
      isSearching &&
      (currently_selected_role === Auth0AccountRole.ORG_ADMIN ||
        currently_selected_role === Auth0AccountRole.ORG_TUTOR)
        ? (searchResults as any[])
        : (suggestedOptions as any[]);
    const cleaned =
      typeOfAttendees === Auth0AccountRole.ORG_TUTOR
        ? removeSelfIfTutor(base)
        : base;

    if (isSearching) {
      return cleaned;
    }
    const label =
      typeOfAttendees === Auth0AccountRole.ORG_TUTOR
        ? "Suggested tutors"
        : "Suggested students";
    return [
      { label, options: cleaned },
      { label: RECENT_LABEL, options: recentOptions },
    ].filter((group) => group.options.length > 0);
  }, [
    isSearching,
    searchResults,
    suggestedOptions,
    recentOptions,
    currently_selected_role,
    typeOfAttendees,
    removeSelfIfTutor,
  ]);

  const noOptionsMessage = useCallback(
    () =>
      currently_selected_role === Auth0AccountRole.ORG_ADMIN ||
      currently_selected_role === Auth0AccountRole.ORG_TUTOR
        ? inputValue.trim().length > 0
          ? "No matches"
          : "Type to search by name"
        : typeOfAttendees === Auth0AccountRole.ORG_TUTOR
        ? "You have been added by default"
        : "No options",
    [currently_selected_role, inputValue, typeOfAttendees]
  );

  const handleLinkToStudentOrTutorPage = (
    attendee:
      | MeetingStudentLimitedResponse
      | MeetingStudentAdminResponse
      | MeetingTutorLimitedResponse
      | MeetingTutorAdminResponse
      | StudentLimitedResponse
      | StudentAdminResponse
      | TutorLimitedResponse
      | TutorAdminResponse
  ) => {
    if (currently_selected_role === Auth0AccountRole.ORG_ADMIN) {
      return window.open(
        `${window.location.origin}${linkPrefix}${getAttendeeId(attendee)}`
      );
    }
    if (currently_selected_role === Auth0AccountRole.ORG_TUTOR) {
      if (linkPrefix === "/manage/students/") {
        return window.open(
          `${window.location.origin}${linkPrefix}${getAttendeeId(attendee)}`
        );
      }
    }
    return;
  };

  const SelectImpl =
    typeOfAttendees === Auth0AccountRole.ORG_TUTOR
      ? SelectWithAvailability
      : Select;

  return (
    <>
      {editing && (
        <SelectImpl
          id={`add-${typeOfAttendees}`}
          name={`add-${typeOfAttendees}`}
          // Sized with Book Meeting's other fields (meetingFields.css).
          classNamePrefix="meeting-field"
          isMulti
          isClearable={false}
          options={groupedOptions as any}
          value={filteredAttendees.map((attendee) =>
            isFixed(attendee) ? { ...attendee, isFixed: true } : attendee
          )}
          getOptionLabel={(a: any) => `${a ? concatenateName(a) : ""}`}
          getOptionValue={(a: any) => (a ? getAttendeeId(a).toString() : "")}
          onChange={handleSelectionChange}
          isDisabled={false}
          placeholder={
            currently_selected_role === Auth0AccountRole.ORG_ADMIN ||
            currently_selected_role === Auth0AccountRole.ORG_TUTOR
              ? "Type name to search…"
              : undefined
          }
          onInputChange={(val: string) => setInputValue(val)}
          noOptionsMessage={noOptionsMessage}
          tutorsWithAvailability={tutorsWithAvailability}
          closeMenuOnSelect={true}
        />
      )}

      {!editing &&
        filteredAttendees.map((attendee, index) => (
          <div className="pb-3" key={`${getAttendeeId(attendee)}-top-${index}`}>
            <MeetingAttendee
              userType={typeOfAttendees}
              key={`${getAttendeeId(attendee)}-attendee-${index}`}
              attendee={attendee}
              newView={true}
              onClick={() => {
                handleLinkToStudentOrTutorPage(attendee);
              }}
              append={append && append[index]}
            >
              {typeOfAttendees === Auth0AccountRole.ME && (
                <CheckboxInput
                  id={getAttendeeId(attendee).toString()}
                  checked={"attended" in attendee ? attendee.attended : false}
                  onCheck={(checked: boolean) => {
                    MeetingsService.editMeetingStudent({
                      meetingStudentId: (attendee as any).id,
                      requestBody: { attended: checked },
                    })
                      .then(() => {
                        announceMeetingsChanged();
                        Notifications.success("Attendance updated!");
                        const updatedAttendees = filteredAttendees.map((a) =>
                          getAttendeeId(a) === getAttendeeId(attendee)
                            ? { ...a, attended: checked }
                            : a
                        );
                        updateAttendees(typeOfAttendees, updatedAttendees);
                      })
                      .catch(() => {
                        Notifications.error(
                          "Attendance could not be updated at this time!"
                        );
                      });
                  }}
                />
              )}
            </MeetingAttendee>
          </div>
        ))}
    </>
  );
}

// -----------------------------------------------------------------------------------------

/**
 * A meeting's subject and people while viewing it: the subject, Approval &
 * billing for admins, the tutors, the students with their attendance, and
 * Message. Book Meeting's editable fields are in MeetingDialog/MeetingFields.
 */
export default function MeetingAttendees({
  tutors,
  students,
  event,
  currentMeetingInfo,
  orgId,
  removeAttendee,
  updateAttendees,
  setEvents,
}: {
  tutors: (
    | MeetingTutorLimitedResponse
    | MeetingTutorAdminResponse
    | TutorLimitedResponse
    | TutorAdminResponse
  )[];
  students: (
    | MeetingStudentLimitedResponse
    | MeetingStudentAdminResponse
    | StudentLimitedResponse
    | StudentAdminResponse
  )[];
  event?: MeetingAdminResponse | MeetingLimitedResponse;
  currentMeetingInfo: MeetingDetails;
  orgId: number;
  removeAttendee: (userType: Auth0AccountRole, id: number) => void;
  updateAttendees: (
    userType: Auth0AccountRole,
    attendees: (
      | MeetingTutorLimitedResponse
      | MeetingStudentLimitedResponse
      | MeetingTutorAdminResponse
      | MeetingStudentAdminResponse
      | StudentLimitedResponse
      | StudentAdminResponse
      | TutorLimitedResponse
      | TutorAdminResponse
    )[]
  ) => void;
  setEvents: (e?: string) => Promise<void>;
}) {
  const { currently_selected_role, currently_selected_organization } =
    useContext(OrgRolesAndAccountContext);

  const [financialsOpen, setFinancialsOpen] = useState<boolean>(false);

  const shared = {
    subject: event?.org_subject,
    editing: false,
    creating: false,
    event,
    currentMeetingInfo,
    orgId,
    remove: removeAttendee,
    updateAttendees,
  };

  return (
    <div className="bg-gray-100 rounded-lg col-span-12 md:col-span-3 m-0">
      <div className="bg-white p-5 rounded-t-lg">
        <div className="flex items-center justify-between gap-2">
          <div className="flex-1 min-w-0">
            <Tag
              color={TagColor.GREEN}
              size={TagSize.NARROW}
              item={event?.org_subject.name as string}
            />
          </div>

          {event && currently_selected_role === Auth0AccountRole.ORG_ADMIN && (
            <Dialog open={financialsOpen} onOpenChange={setFinancialsOpen}>
              <DialogTrigger asChild>
                <button
                  type="button"
                  title="Approval & billing"
                  aria-label="Approval & billing"
                  className="w-6 h-6 rounded-full border border-gray-300 bg-white text-gray-500 hover:text-green-600 hover:border-green-500 flex items-center justify-center flex-shrink-0"
                >
                  <FaDollarSign size={12} />
                </button>
              </DialogTrigger>
              {financialsOpen && (
                <MeetingFinancialsDialog
                  meetingId={event.id}
                  setEvents={setEvents}
                />
              )}
            </Dialog>
          )}
        </div>
      </div>

      <div className="p-5">
        <div className="pb-2">
          <h2 className="font-bold mb-1">Tutors</h2>
          <AttendeesList
            {...shared}
            attendees={tutors ?? []}
            typeOfAttendees={Auth0AccountRole.ORG_TUTOR}
            linkPrefix={"/manage/tutors/"}
          />
        </div>

        <div className="flex justify-between">
          <h2 className="font-bold mb-1">Students</h2>
          <h2 className="mb-1 mt-4 text-sm">Attendance</h2>
        </div>

        <AttendeesList
          {...shared}
          attendees={students ?? []}
          typeOfAttendees={Auth0AccountRole.ME}
          linkPrefix={"/manage/students/"}
        />

        <Dialog>
          <DialogTrigger asChild>
            <Button
              extraClasses="mt-5 w-full bg-white"
              color={ButtonColor.GREEN}
              fill={ButtonFill.DEFAULT}
              size={ButtonSize.SMALL}
            >
              <p className="font-semibold">Message</p>
            </Button>
          </DialogTrigger>
          <ComposeMessageDialog
            students={students}
            tutors={tutors}
            org_id={
              event?.org_id ?? (currently_selected_organization as number)
            }
          />
        </Dialog>
      </div>
    </div>
  );
}
